import DOMPurify from 'dompurify'

/**
 * Strips HTML tags from a string and returns the plain text content.
 * This function safely handles HTML content by using the browser's DOM parser.
 *
 * @param html - The HTML string to strip tags from
 * @returns The plain text content without HTML tags
 * @example
 * ```ts
 * const html = '<p>Hello <b>World</b>!</p>'
 * const text = stripHTML(html) // Returns: "Hello World!"
 * ```
 */
export function stripHTML(html: string): string {
  if (!html) {
    return ''
  }

  const div = document.createElement('div')
  div.innerHTML = html
  const text = div.textContent?.trim() || div.innerText?.trim() || ''
  return text.replace(/\s+/g, ' ')
}

/**
 * Converts plain text with newline characters to HTML paragraphs, or returns
 * sanitized HTML content if already formatted.
 *
 * @param content - Plain text with newlines or HTML content
 * @returns Sanitized HTML-formatted content
 *
 * @example
 * ```ts
 * const plainText = 'First paragraph\n\nSecond paragraph'
 * plainTextToHTML(plainText)
 * // Returns: '<p>First paragraph</p><p>Second paragraph</p>'
 *
 * const htmlText = '<p>Already <strong>HTML</strong></p>'
 * plainTextToHTML(htmlText)
 * // Returns: '<p>Already <strong>HTML</strong></p>' (sanitized)
 * ```
 */
export function plainTextToHTML(content: string): string {
  if (!content) {
    return ''
  }

  const trimmedContent = content.trim()
  const htmlBlockTags = /^<(p|div|h[1-6]|ul|ol|li|blockquote|pre|table)/i
  const hasHTMLStructure = htmlBlockTags.test(trimmedContent)

  if (hasHTMLStructure) {
    return DOMPurify.sanitize(trimmedContent)
  }

  const paragraphs = trimmedContent
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)

  if (paragraphs.length === 0) {
    return ''
  }

  // Escape HTML special characters in plain text to prevent XSS
  const escapeHTML = (text: string): string => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  return paragraphs.map((p) => `<p>${escapeHTML(p)}</p>`).join('')
}
